import React from 'react';
import { Box, Typography, IconButton, Tooltip } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import { WandIcon } from '../components/icons/WandIcon';
import { browserHistory } from '../components/AppContent';
import { FOOTER_HEIGHT } from '../components/AppFooter';
import { linkToPage } from '../App';
import { DocumentAiSession, useAiDocumentChat, getDocumentAiDisplayState } from './AiDocumentChatContext';
import { AiProcessingIndicator } from './AiProcessingIndicator';
import { AI_ACCENT, AI_ACCENT_SOFT } from './aiTheme';

interface AiChatMinimizedBarProps {
    /** Current width of the docked nav sidebar (0px/SEMI_WIDTH/FULL_WIDTH from
     * MainDrawer.tsx, per dockMode) - explicit bug report: a flow-positioned bar was ending up
     * underneath the sidebar when it's open (the sidebar's own sticky positioning stacked over
     * it), so this is fixed to the viewport instead, with left/right insets computed by the
     * caller (AppContent.tsx, the one place that already tracks dockMode/Quick View state) to
     * stay clear of both the sidebar and the Quick View panel exactly, rather than participating
     * in <main>'s own flex flow. */
    leftInset: string;
    /** Current width of the docked Quick View panel (0px or QUICK_VIEW_WIDTH). */
    rightInset: string;
}

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'fixed',
            // Directly above the footer, minimal gap per explicit request (was reading as too
            // much space) - 6px, not a full theme.spacing unit.
            bottom: `calc(${FOOTER_HEIGHT} + 6px)`,
            zIndex: 1000, // below the nav sidebar's own overlay/backdrop (1007/1008 in MainDrawer.tsx) so an open overlay menu still covers this the same way it covers the rest of the page, rather than a partial/broken overlap.
            display: 'flex',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
            padding: theme.spacing(0, 2),
            pointerEvents: 'none',
            transition: 'left 0.2s ease, right 0.2s ease',
            '& > *': {
                pointerEvents: 'auto',
            },
        },
        pill: {
            display: 'flex',
            alignItems: 'center',
            gap: theme.spacing(1),
            backgroundColor: '#fff',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 16,
            padding: theme.spacing(0.5, 0.75, 0.5, 1),
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            cursor: 'pointer',
            maxWidth: 220,
        },
        titleStack: {
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            lineHeight: 1.25,
        },
        iconChip: {
            width: 22,
            height: 22,
            borderRadius: '50%',
            backgroundColor: AI_ACCENT_SOFT,
            color: AI_ACCENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& .MuiSvgIcon-root': { fontSize: 13 },
        },
        titleLine1: {
            fontSize: 12,
            fontWeight: 700,
            color: theme.palette.text.primary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        // Only shown while a run is actively processing, as the live "X% complete" readout -
        // explicit request so the percentage is visible without reopening the panel.
        titleLine2: {
            fontSize: 10,
            fontWeight: 500,
            color: theme.palette.text.secondary,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        },
        statusIcon: {
            display: 'flex',
            flexShrink: 0,
            '& .MuiSvgIcon-root': { fontSize: 15 },
        },
        statusReady: {
            color: theme.palette.success.main,
        },
        statusError: {
            color: theme.palette.error.main,
        },
        actionBtn: {
            padding: 3,
            flexShrink: 0,
            color: theme.palette.text.disabled,
            '&:hover': {
                color: theme.palette.text.primary,
            },
        },
    })
);

// See AiChatDialog.tsx's identical helper for why this uses the /enquiriesSummary/:id route.
const navigateToEnquiry = (enquiryGuid: string) => {
    browserHistory.push(`${linkToPage('enquiriesSummary')}/${encodeURIComponent(enquiryGuid)}`);
};

const StatusIcon: React.FC<{ session: DocumentAiSession; className: string }> = ({ session, className }) => {
    if (getDocumentAiDisplayState(session) === 'failed') {
        return <ErrorOutlineIcon className={className} />;
    }
    return <CheckCircleIcon className={className} />;
};

export const AiChatMinimizedBar: React.FC<AiChatMinimizedBarProps> = ({ leftInset, rightInset }) => {
    const classes = useStyles();
    const { sessions, openSession, closeSession } = useAiDocumentChat();
    // Explicit bug report: opening an enquiry that already had a saved assessment put a chip
    // here even though the user never touched the AI badge - ensureStatusChecked's own silent
    // background hydration creates a session exactly like any other (defaulting isMinimized:
    // true), so filtering on isMinimized alone surfaced it anyway. isActiveChip (see its own
    // comment in AiDocumentChatContext.tsx) is only ever true once the user has genuinely
    // interacted with this session - never set by silent hydration alone.
    const minimizedSessions = sessions.filter(session => session.isMinimized && session.isActiveChip);

    if (minimizedSessions.length === 0) {
        return null;
    }

    return (
        <Box className={classes.root} style={{ left: leftInset, right: rightInset }}>
            {minimizedSessions.map(session => {
                const displayState = getDocumentAiDisplayState(session);
                const isProcessing = displayState === 'processing' || displayState === 'processingWithResults';
                return (
                <Box key={session.key} className={classes.pill} onClick={() => openSession(session.key)}>
                    {isProcessing ? (
                        <AiProcessingIndicator size={20} />
                    ) : (
                        <Box className={classes.iconChip}>
                            <WandIcon />
                        </Box>
                    )}
                    <Box className={classes.titleStack}>
                        <Typography className={classes.titleLine1} title={session.enquirySummary.name}>
                            {session.enquirySummary.name}
                        </Typography>
                        {isProcessing && session.progressPercent != null && (
                            <Typography className={classes.titleLine2}>{session.progressPercent}% complete</Typography>
                        )}
                    </Box>
                    {!isProcessing && (
                        <Box
                            className={[
                                classes.statusIcon,
                                displayState === 'ready' ? classes.statusReady : '',
                                displayState === 'failed' ? classes.statusError : '',
                            ].join(' ')}
                        >
                            <StatusIcon session={session} className="" />
                        </Box>
                    )}
                    <Tooltip title="Open this enquiry">
                        <IconButton
                            size="small"
                            className={classes.actionBtn}
                            onClick={e => { e.stopPropagation(); navigateToEnquiry(session.enquiryGuid); }}
                        >
                            <OpenInNewIcon style={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Close">
                        <IconButton
                            size="small"
                            className={classes.actionBtn}
                            onClick={e => { e.stopPropagation(); closeSession(session.key); }}
                        >
                            <CloseIcon style={{ fontSize: 14 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
                );
            })}
        </Box>
    );
};
